import { useEffect, useRef, useState } from "react"
import { Dayjs } from "dayjs"
import styled from "styled-components"

import { UITypes } from "types"

import { getDateRangeFromOption } from "utils/dateUtils"
import DateRangePicker from "modals/StatsReportModal/DateRangePicker"
import { SelectStatic } from "components/form/SelectStatic"

const SelectWrapper = styled.div`
  position: relative;
  min-width: 150px;

  .static-select__control {
    min-width: 150px;
    display: flex;
    min-height: 32px !important;
  }
`

const DateRangePickerContainer = styled.div<{ position: "top" | "bottom" }>`
  > div > div {
    right: 0px;

    ${({ position }) => (position === "bottom" ? `top: 37px;` : `bottom: 5px;`)}
  }
`

const DateRangeContainer = styled.div`
  padding-left: 10px;
  font-size: 14px;
`

export type DateRange = [string, string]

interface DateRangePickerSelectProps {
  options: UITypes.Option[]
  defaultOption: UITypes.Option
  onSelectDateRange: (dateRange: DateRange) => void
  onOptionChange?: (option: UITypes.Option) => void
  minDate?: string
}

export const DateRangePickerSelect = ({
  options,
  defaultOption,
  onSelectDateRange,
  onOptionChange,
  minDate,
}: DateRangePickerSelectProps) => {
  const selectKey = useRef(1)
  const selectWrapperRef = useRef<HTMLDivElement | null>(null)
  const [selectedOption, setSelectedOption] = useState(defaultOption)
  const [selectedDateRange, setSelectedDateRange] = useState<[Dayjs, Dayjs] | null>(null)
  const [showCustomDatePicker, setShowCustomDatePicker] = useState(false)
  const [position, setPosition] = useState<"top" | "bottom">("top")

  useEffect(() => {
    const calculatePosition = () => {
      if (!selectWrapperRef.current) return

      const boundary = selectWrapperRef.current.getBoundingClientRect()

      if (boundary.top < 455) {
        setPosition("bottom")
      } else {
        setPosition("top")
      }
    }

    document.addEventListener("scroll", calculatePosition)

    return () => {
      document.removeEventListener("scroll", calculatePosition)
    }
  }, [])

  const isCustom = selectedOption.value === "custom"

  const handleDateCancel = () => {
    selectKey.current += 1
    setShowCustomDatePicker(false)
  }

  const handleDateRangeSubmit = (dateRangeValue: [Dayjs, Dayjs]) => {
    setSelectedDateRange(dateRangeValue)
    setShowCustomDatePicker(false)
    setSelectedOption(options[options.length - 1] as UITypes.Option)
    onSelectDateRange([dateRangeValue[0].format("YYYY-MM-DD"), dateRangeValue[1].format("YYYY-MM-DD")])
  }

  const handleOptionChange = (value: UITypes.Option[]) => {
    const option = value[0]
    if (!option) return

    if (String(option.value) === "custom") {
      setSelectedOption({ value: "custom", label: "Custom" })
      setTimeout(() => setShowCustomDatePicker(true), 0)
      return
    }

    if (
      option.value === "all" ||
      option.value === "last-year" ||
      option.value === "this-year" ||
      option.value === "last-month"
    ) {
      setSelectedOption(option)
      onSelectDateRange(getDateRangeFromOption(String(option.value)))
      return
    }

    setSelectedDateRange(null)
    setSelectedOption(option)
    onOptionChange?.(option)
    onSelectDateRange(getDateRangeFromOption(String(option.value)))
  }

  const contentRenderer = () => {
    if (isCustom && selectedDateRange) {
      return () => (
        <DateRangeContainer>
          {selectedDateRange[0].format("MMM D, YYYY")}-{selectedDateRange[1].format("MMM D, YYYY")}
        </DateRangeContainer>
      )
    }
    return undefined
  }

  return (
    <SelectWrapper ref={selectWrapperRef}>
      {showCustomDatePicker && (
        <DateRangePickerContainer position={position}>
          <DateRangePicker
            minDate={minDate}
            onCancel={handleDateCancel}
            onSubmit={handleDateRangeSubmit}
            selectedRange={selectedDateRange || undefined}
          />
        </DateRangePickerContainer>
      )}
      <SelectStatic
        key={selectKey.current}
        values={[selectedOption]}
        options={options}
        onChange={handleOptionChange}
        contentRenderer={contentRenderer()}
        isSearchable={false}
      />
    </SelectWrapper>
  )
}
